import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { Avatar } from "../components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Inbox, Loader2, Crown, Shield, Gavel, UserPlus, Flag, Check, MessagesSquare, Send, Pencil, X, Eye, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { reportRank, isUrgentReport } from "../components/ReportDialog";

const REPORT_STATUS = { pending_review: "Pending Review", open: "Pending Review", in_progress: "In Progress", resolved: "Resolved", dismissed: "Dismissed" };
const REPORT_STATUS_COLOR = { pending_review: "#E19C00", open: "#E19C00", in_progress: "#520202", resolved: "#2e7d32", dismissed: "#657786" };
const STATUS_OPTIONS = { pending_review: "Pending Review", in_progress: "In Progress", resolved: "Resolved", dismissed: "Dismissed" };

const SUB_STATUS_COLOR = { pending: "#E19C00", active: "#2e7d32", cancelled: "#657786" };
const SUB_STATUS_LABEL = { pending: "Pending Payment", active: "Active", cancelled: "Cancelled" };

function PremiumPanel() {
  const { user } = useAuth();
  const canManage = user.role === "owner" || user.is_admin;
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [subs, setSubs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/subscriptions"); setSubs(data); } catch (e) {}
    try { const { data } = await api.get("/site-content"); setTiers(data.subscription_tiers || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);
  const manage = async (userId, action, tierKey) => {
    setBusy(userId + action);
    try {
      await api.post(`/admin/subscriptions/${userId}`, { action, tier_key: tierKey });
      toast.success(action === "cancel" ? "Membership cancelled" : "Membership activated");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const setPayment = async (userId, patch) => {
    const cur = subs.find((s) => s.user_id === userId) || {};
    const paid = patch.paid !== undefined ? patch.paid : cur.paid_status === "paid";
    const month = patch.month !== undefined ? patch.month : (cur.paid_month || null);
    setSubs((prev) => prev.map((s) => s.user_id === userId ? { ...s, paid_status: paid ? "paid" : "unpaid", paid_month: month } : s));
    try { await api.post(`/admin/subscriptions/${userId}/payment`, { paid, month }); toast.success("Payment status updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); load(); }
  };
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="premium-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><Crown className="w-5 h-5" style={{ color: "#E19C00" }} />Premium Members</h3>
      <p className="text-xs text-spotter-maroon/70 mb-3">Payments are manual (PayPal/Venmo). Activate a member after you confirm their payment. They'll get a notification, DM & email, plus an auto reminder before renewal.</p>
      {subs.length === 0 ? <p className="text-sm text-spotter-maroon">No membership requests yet.</p> : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.user_id} className="border-2 border-black rounded-lg p-3 bg-spotter-cream" data-testid={`sub-${s.user_id}`}>
              <div className="flex items-center gap-3">
                <Avatar user={s.user} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{s.user?.name} <span className="text-spotter-maroon/60 font-normal">@{s.user?.username}</span></div>
                  <div className="text-xs text-spotter-maroon flex items-center gap-1.5 flex-wrap">
                    <Crown className="w-3 h-3" style={{ color: "#E19C00" }} />{s.tier_name} · ${s.price}
                    {s.next_due && s.status === "active" && <span>· renews {new Date(s.next_due).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full border-2 border-black text-white" style={{ background: SUB_STATUS_COLOR[s.status] || "#657786" }}>{SUB_STATUS_LABEL[s.status] || s.status}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ background: s.paid_status === "paid" ? "#2e7d32" : "#E19C00", color: "#fff" }}>{s.paid_status === "paid" ? `Paid${s.paid_month ? " · " + s.paid_month : ""}` : "Unpaid"}</span>
                </div>
              </div>
              {(s.features || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.features.slice(0, 4).map((f, i) => <span key={i} className="text-[10px] font-semibold border border-black/40 rounded-full px-2 py-0.5 bg-white">{f}</span>)}
                  {s.features.length > 4 && <span className="text-[10px] text-spotter-maroon/60">+{s.features.length - 4} more</span>}
                </div>
              )}
              {canManage && (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <div className="w-32">
                      <Select value={s.paid_status === "paid" ? "paid" : "unpaid"} onValueChange={(v) => setPayment(s.user_id, { paid: v === "paid" })}>
                        <SelectTrigger data-testid={`sub-paid-${s.user_id}`} className="border-2 border-black bg-white h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="w-36">
                      <Select value={s.paid_month || undefined} onValueChange={(v) => setPayment(s.user_id, { month: v })}>
                        <SelectTrigger data-testid={`sub-month-${s.user_id}`} className="border-2 border-black bg-white h-9 text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
                        <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <div className="w-40">
                      <Select value={s.tier_key} onValueChange={(v) => manage(s.user_id, s.status === "active" ? "change" : "activate", v)}>
                        <SelectTrigger data-testid={`sub-tier-${s.user_id}`} className="border-2 border-black bg-white h-9 text-xs"><SelectValue placeholder="Choose tier" /></SelectTrigger>
                        <SelectContent>{tiers.map((t) => <SelectItem key={t.key} value={t.key}>{t.name} — ${t.price}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {s.status !== "active" && (
                      <Button size="sm" data-testid={`sub-activate-${s.user_id}`} disabled={busy === s.user_id + "activate"} onClick={() => manage(s.user_id, "activate", s.tier_key)}
                        className="bg-spotter-maroon text-spotter-cream border-2 border-black text-xs gap-1"><Check className="w-3.5 h-3.5" />Activate</Button>
                    )}
                    {s.status === "active" && (
                      <Button size="sm" variant="outline" data-testid={`sub-cancel-${s.user_id}`} disabled={busy === s.user_id + "cancel"} onClick={() => manage(s.user_id, "cancel")}
                        className="border-2 border-black text-xs">Cancel</Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportsPanel() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const load = async () => { try { const { data } = await api.get("/reports"); setReports(data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/reports/${id}`, { status }); setReports((p) => p.map((r) => r.id === id ? { ...r, status } : r)); toast.success("Report updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  // Priority sort: urgency first (DMCA/Harassment top), then newest.
  const sorted = [...reports].sort((a, b) => {
    const ra = reportRank(a.category), rb = reportRank(b.category);
    if (ra !== rb) return ra - rb;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="reports-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Flag className="w-5 h-5" />Member Reports</h3>
      {sorted.length === 0 ? <p className="text-sm text-spotter-maroon">No reports yet.</p> : (
        <div className="space-y-3">
          {sorted.map((r) => {
            const urgent = isUrgentReport(r.category);
            const isExternal = r.target_type === "external";
            return (
            <div key={r.id} className={`rounded-lg p-3 ${urgent ? "border-4 border-red-600 bg-red-50" : "border-2 border-black bg-spotter-cream"}`} data-testid={`report-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-xs font-bold border-2 rounded-full px-2 py-0.5 ${urgent ? "border-red-600 bg-red-600 text-white" : "border-black bg-spotter-gold/40"}`} data-testid={`report-cat-badge-${r.id}`}>
                  {urgent && <AlertTriangle className="w-3.5 h-3.5" />}{r.category}
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full border-2 border-black text-white" style={{ background: REPORT_STATUS_COLOR[r.status] || "#E19C00" }} data-testid={`report-status-badge-${r.id}`}>{REPORT_STATUS[r.status] || "Pending Review"}</span>
              </div>
              <div className="text-sm mt-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-spotter-maroon text-spotter-cream rounded px-1.5 py-0.5 mr-1">{isExternal ? "DMCA / External" : r.target_type || "user"}</span>
                {isExternal ? (
                  r.content_url
                    ? <a href={r.content_url} target="_blank" rel="noopener noreferrer" className="font-bold underline inline-flex items-center gap-1" data-testid={`report-target-${r.id}`}><Link2 className="w-3.5 h-3.5" />{r.content_url}</a>
                    : <span className="font-bold" data-testid={`report-target-${r.id}`}>{r.reported_username ? `@${r.reported_username}` : "External report"}</span>
                ) : (
                  <button className="font-bold underline" data-testid={`report-target-${r.id}`} onClick={() => r.target?.link && navigate(r.target.link)}>{r.target?.label || r.reported_user?.name || "Unknown"}</button>
                )}
                <span className="text-spotter-maroon"> · reported by {r.reporter_user?.name || r.reporter_name || r.reporter_email || "A guest"} · {new Date(r.created_at).toLocaleString()}</span>
              </div>
              {r.target?.snippet && <p className="text-xs italic text-spotter-maroon mt-1 bg-white border-2 border-black rounded p-2">"{r.target.snippet}"</p>}
              {r.details && <p className="text-sm mt-1 whitespace-pre-wrap bg-white border-2 border-black rounded p-2">{r.details}</p>}
              {r.image_url && <img src={mediaUrl(r.image_url)} alt="evidence" data-testid={`report-image-${r.id}`} className="mt-2 max-h-40 rounded-lg border-2 border-black" />}
              <div className="mt-2 sm:w-48">
                <Select value={STATUS_OPTIONS[r.status] ? r.status : "pending_review"} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger data-testid={`report-status-${r.id}`} className="border-2 border-black bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_OPTIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

const STATUS = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const STATUS_COLOR = { open: "#E19C00", in_progress: "#520202", resolved: "#2e7d32" };
const ROLE_ICON = { owner: Crown, admin: Shield, moderator: Gavel };

function TeamPanel() {
  const { user, patchUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [uname, setUname] = useState("");
  const [role, setRole] = useState("admin");
  const [frames, setFrames] = useState([]);
  const isOwner = user.role === "owner";
  const load = async () => { try { const { data } = await api.get("/staff"); setStaff(data); } catch (e) {} };
  useEffect(() => { load(); api.get("/users/me/frames").then(({ data }) => setFrames(data)).catch(() => {}); }, []);
  const chooseFrame = async (key) => {
    try { const { data } = await api.post("/users/me/frame", { frame: key }); patchUser({ profile_frame: data.profile_frame, profile_frame_url: data.profile_frame_url }); load(); toast.success(key ? "Frame applied!" : "Frame removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const add = async () => {
    if (!uname.trim()) return;
    try { await api.post("/staff/add", { username: uname.trim(), role }); setUname(""); load(); toast.success("Team member added"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const setUserRole = async (id, r) => {
    try { await api.post("/staff/set-role", { user_id: id, role: r }); load(); toast.success("Role updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="team-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3">Your Team</h3>
      {isOwner && (
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input data-testid="add-staff-username" value={uname} onChange={(e) => setUname(e.target.value)} placeholder="member username" className="border-2 border-black" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="add-staff-role" className="border-2 border-black bg-spotter-cream sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="moderator">Moderator</SelectItem></SelectContent>
          </Select>
          <Button data-testid="add-staff-btn" onClick={add} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><UserPlus className="w-4 h-4" />Add</Button>
        </div>
      )}
      <div className="space-y-2">
        {staff.map((s) => {
          const Icon = ROLE_ICON[s.role] || Shield;
          return (
            <div key={s.id} className="flex flex-wrap items-center gap-3 border-2 border-black rounded-lg p-2 bg-spotter-cream" data-testid={`staff-${s.id}`}>
              <Avatar user={s.id === user.id ? user : s} size="sm" showPresence />
              <div className="flex-1"><div className="font-bold text-sm">{s.name}</div><div className="text-xs text-spotter-maroon">@{s.username}</div></div>
              <span className="inline-flex items-center gap-1 text-xs font-bold border-2 border-black rounded-full px-2 py-0.5 bg-spotter-gold/40 capitalize"><Icon className="w-3 h-3" />{s.role}</span>
              {isOwner && s.role !== "owner" && (
                <Button size="sm" variant="outline" data-testid={`remove-staff-${s.id}`} onClick={() => setUserRole(s.id, "member")} className="border-2 border-black text-xs">Remove</Button>
              )}
              {s.id === user.id && frames.length > 0 && (
                <div className="w-full flex items-center gap-2 pt-1 border-t border-black/20" data-testid="staff-frame-picker">
                  <span className="text-xs font-bold text-spotter-maroon">Your frame:</span>
                  <button data-testid="staff-frame-none" onClick={() => chooseFrame(null)} title="No frame"
                    className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${!user.profile_frame ? "border-spotter-maroon" : "border-black"}`}>None</button>
                  {frames.map((f) => (
                    <button key={f.key} data-testid={`staff-frame-${f.key}`} onClick={() => chooseFrame(f.key)} title={f.name}
                      className={`w-10 h-10 rounded-lg border-2 bg-white p-0.5 ${user.profile_frame === f.key ? "border-spotter-maroon ring-2 ring-spotter-gold" : "border-black"}`}>
                      <img src={f.url} alt={f.name} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeaderApplications() {
  const [apps, setApps] = useState([]);
  const load = async () => { try { const { data } = await api.get("/leader/applications"); setApps(data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const respond = async (user_id, action) => {
    try { await api.post("/leader/applications/respond", { user_id, action }); load(); toast.success(action === "approve" ? "Approved as Community Leader" : "Denied"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (apps.length === 0) return null;
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="leader-applications">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3">Community Leader Applications</h3>
      <div className="space-y-2">
        {apps.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border-2 border-black rounded-lg p-2 bg-spotter-cream" data-testid={`leader-app-${a.user_id}`}>
            <div className="flex-1"><div className="font-bold text-sm">{a.user_name}</div><div className="text-xs text-spotter-maroon">{a.reason}</div></div>
            <Button size="sm" data-testid={`approve-leader-${a.user_id}`} onClick={() => respond(a.user_id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Approve</Button>
            <Button size="sm" variant="outline" data-testid={`deny-leader-${a.user_id}`} onClick={() => respond(a.user_id, "deny")} className="border-2 border-black">Deny</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const ROLE_BADGE = { owner: { label: "Owner", icon: Crown, color: "#E19C00" }, admin: { label: "Admin", icon: Shield, color: "#520202" }, moderator: { label: "Mod", icon: Gavel, color: "#2e7d32" } };

function ProfileViewsPanel() {
  const navigate = useNavigate();
  const [views, setViews] = useState([]);
  useEffect(() => { api.get("/admin/profile-views").then(({ data }) => setViews(data)).catch(() => {}); }, []);
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="profile-views-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Eye className="w-5 h-5" style={{ color: "#E19C00" }} />Profile Views</h3>
      <p className="text-xs text-spotter-maroon mb-3">Who viewed whose profile (member views only — Owner, admin & moderator visits are excluded).</p>
      {views.length === 0 ? <p className="text-sm text-spotter-maroon">No member profile views recorded yet.</p> : (
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {views.map((v, i) => (
            <div key={i} className="flex items-center gap-2 border-2 border-black rounded-lg p-2 bg-spotter-cream text-sm" data-testid={`admin-view-${i}`}>
              <button onClick={() => navigate(`/profile/${v.viewer.id}`)} className="font-semibold hover:underline">{v.viewer.name}</button>
              <span className="text-spotter-maroon">viewed</span>
              <button onClick={() => navigate(`/profile/${v.profile.id}`)} className="font-semibold hover:underline">{v.profile.name}</button>
              <span className="ml-auto text-xs text-spotter-maroon shrink-0">{v.count > 1 ? `${v.count}× · ` : ""}{v.last_viewed ? new Date(v.last_viewed).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegalNameRequestsPanel() {
  const [reqs, setReqs] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/legal-name-requests"); setReqs(data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const respond = async (id, action) => {
    try { await api.post("/admin/legal-name-requests/respond", { id, action }); setReqs((p) => p.filter((r) => r.id !== id)); toast.success(action === "approve" ? "Legal name updated" : "Request denied"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="legal-name-requests-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><Shield className="w-5 h-5" style={{ color: "#E19C00" }} />Legal Name Change Requests</h3>
      <p className="text-xs text-spotter-maroon mb-3">Review the documentation, then update the member's legal name (24–48 hour SLA).</p>
      {reqs.length === 0 ? <p className="text-sm text-spotter-maroon">No pending legal name changes.</p> : (
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="border-2 border-black rounded-lg p-3 bg-spotter-cream" data-testid={`legal-req-${r.id}`}>
              <div className="text-sm mb-1"><span className="font-semibold">{r.current_legal_name || r.user_name}</span> → <span className="font-bold text-spotter-maroon">"{r.requested_legal_name}"</span></div>
              <p className="text-xs text-spotter-maroon mb-1"><b>Message:</b> {r.message}</p>
              <a href={r.document_url} target="_blank" rel="noreferrer" className="text-xs font-bold underline text-spotter-maroon" data-testid={`legal-doc-${r.id}`}>View submitted document</a>
              <div className="text-[11px] text-spotter-maroon/70 mt-1">{new Date(r.created_at).toLocaleString()}</div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" data-testid={`legal-approve-${r.id}`} onClick={() => respond(r.id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Update name</Button>
                <Button size="sm" variant="outline" data-testid={`legal-deny-${r.id}`} onClick={() => respond(r.id, "deny")} className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NameRequestsPanel() {
  const [reqs, setReqs] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/name-requests"); setReqs(data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const respond = async (user_id, action) => {
    try { await api.post("/admin/name-requests/respond", { user_id, action }); setReqs((p) => p.filter((r) => r.user_id !== user_id)); toast.success(action === "approve" ? "Name change approved" : "Name change denied"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="name-requests-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Pencil className="w-5 h-5" style={{ color: "#E19C00" }} />Name Change Requests</h3>
      {reqs.length === 0 ? <p className="text-sm text-spotter-maroon">No pending name changes.</p> : (
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-2 border-black rounded-lg p-3 bg-spotter-cream" data-testid={`name-req-${r.user_id}`}>
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-semibold">{r.current_name}</span>
                <span className="text-spotter-maroon"> wants to change to </span>
                <span className="font-bold text-spotter-maroon">"{r.requested_name}"</span>
                <div className="text-xs text-spotter-maroon/70">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <Button size="sm" data-testid={`approve-name-${r.user_id}`} onClick={() => respond(r.user_id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
              <Button size="sm" variant="outline" data-testid={`deny-name-${r.user_id}`} onClick={() => respond(r.user_id, "deny")} className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupChat() {
  const { user } = useAuth();
  const { registerGroupHandler, registerTypingHandler, registerReadHandler, sendTyping } = useRealtime();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [typers, setTypers] = useState({});
  const endRef = useRef(null);
  const typingTimer = useRef();
  const typerTimers = useRef({});
  const markRead = async () => { try { await api.post("/admin/group-chat/read"); } catch (e) {} };
  const load = async () => { try { const { data } = await api.get("/admin/group-chat"); setMsgs(data); markRead(); } catch (e) {} };
  useEffect(() => { load(); }, []);
  useEffect(() => registerGroupHandler((m) => { setMsgs((p) => p.some((x) => x.id === m.id) ? p : [...p, m]); markRead(); }), [registerGroupHandler]);
  useEffect(() => registerTypingHandler((t) => {
    if (t.channel !== "team" || t.from === user.id) return;
    setTypers((prev) => { const n = { ...prev }; if (t.typing) n[t.from] = t.from_name || "Someone"; else delete n[t.from]; return n; });
    clearTimeout(typerTimers.current[t.from]);
    if (t.typing) typerTimers.current[t.from] = setTimeout(() => setTypers((prev) => { const n = { ...prev }; delete n[t.from]; return n; }), 3000);
  }), [registerTypingHandler, user.id]);
  useEffect(() => registerReadHandler((r) => { if (r.channel === "team") load(); }), [registerReadHandler]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, typers]);
  const onType = (e) => {
    setText(e.target.value);
    clearTimeout(typingTimer.current);
    sendTyping(null, true, "team");
    typingTimer.current = setTimeout(() => sendTyping(null, false, "team"), 1500);
  };
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/group-chat", { text: t }); setMsgs((p) => p.some((x) => x.id === data.id) ? p : [...p, data]); setText(""); sendTyping(null, false, "team"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const lastMine = [...msgs].reverse().find((m) => m.sender_id === user.id);
  const seenCount = lastMine ? (lastMine.read_by || []).filter((uid) => uid !== user.id).length : 0;
  return (
    <div className="bg-white border-2 border-black rounded-xl p-4 mb-6 brand-shadow-sm" data-testid="group-chat-panel">
      <h3 className="font-display text-2xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><MessagesSquare className="w-5 h-5" />Team Chat</h3>
      <p className="text-xs text-spotter-maroon/70 mb-3">A private channel for the whole team — owner, admins & moderators.</p>
      <div data-testid="group-chat-messages" className="border-2 border-black rounded-lg bg-spotter-cream p-3 h-72 overflow-y-auto space-y-3">
        {msgs.length === 0 ? <p className="text-sm text-spotter-maroon text-center py-8">No messages yet — say hi to the team! 🐆</p> : msgs.map((m) => {
          const mine = m.sender_id === user.id;
          const rb = ROLE_BADGE[m.sender_role] || null;
          return (
            <div key={m.id} data-testid={`group-msg-${m.id}`} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
              <Avatar user={{ id: m.sender_id, name: m.sender_name, avatar_url: m.sender_avatar }} size="sm" />
              <div className={`max-w-[75%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                <div className={`flex items-center gap-1.5 text-xs mb-0.5 ${mine ? "flex-row-reverse" : ""}`}>
                  <span className="font-bold text-spotter-maroon">{mine ? "You" : m.sender_name}</span>
                  {rb && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-white rounded px-1 py-0.5" style={{ background: rb.color }}><rb.icon className="w-2.5 h-2.5" />{rb.label}</span>}
                  <span className="text-spotter-maroon/50">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className={`rounded-2xl px-3 py-2 text-sm border-2 border-black ${mine ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
                  {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
                  {m.image_url && <img src={mediaUrl(m.image_url)} alt="" className="mt-1 max-h-40 rounded-lg" />}
                </div>
              </div>
            </div>
          );
        })}
        {seenCount > 0 && <div data-testid="team-seen" className="text-[11px] text-spotter-maroon/60 text-right pr-1">Seen by {seenCount}</div>}
        {Object.keys(typers).length > 0 && <div data-testid="team-typing-indicator" className="text-xs text-spotter-maroon italic">{Object.values(typers).join(", ")} {Object.keys(typers).length > 1 ? "are" : "is"} typing…</div>}
        <div ref={endRef} />
      </div>
      <div className="mt-3 flex gap-2">
        <Input data-testid="group-chat-input" value={text} onChange={onType}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Message the team…" className="border-2 border-black" />
        <Button data-testid="group-chat-send" onClick={send} disabled={busy || !text.trim()} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Send className="w-4 h-4" />Send</Button>
      </div>
    </div>
  );
}

export default function AdminInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const isFull = user.is_admin || user.role === "owner";
  const isTeam = isFull || user.role === "moderator";

  useEffect(() => {
    if (!isTeam) { navigate("/feed"); return; }
    if (isFull) api.get("/complaints").then(({ data }) => setItems(data)).catch(() => setItems([]));
    else setItems([]);
  }, []);

  const update = async (id, patch) => {
    try { const { data } = await api.put(`/complaints/${id}`, patch);
      setItems((prev) => prev.map((c) => (c.id === id ? data : c))); toast.success("Updated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (items === null) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="font-display text-3xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><Inbox className="w-7 h-7" />{isFull ? "Admin Inbox" : "Team Inbox"}</h2>
      <p className="text-spotter-maroon mb-5">{isFull ? "Your direct line — member complaints land here." : "Chat with the rest of the Spotter team."}</p>
      <GroupChat />
      {isFull && <>
        <TeamPanel />
        <PremiumPanel />
        <ProfileViewsPanel />
        <NameRequestsPanel />
        <LegalNameRequestsPanel />
        <LeaderApplications />
        <ReportsPanel />
        {items.length === 0 ? <p className="text-spotter-maroon">No complaints yet.</p> : (
          <div className="space-y-4">
            {items.map((c) => (
              <div key={c.id} className="bg-white border-2 border-black rounded-xl p-4 brand-shadow-sm" data-testid={`complaint-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-bold">{c.subject}</div><div className="text-xs text-spotter-maroon">from {c.user_name} · {new Date(c.created_at).toLocaleString()}</div></div>
                  <span className="text-xs font-bold px-2 py-1 rounded-full border-2 border-black text-white" style={{ background: STATUS_COLOR[c.status] }}>{STATUS[c.status]}</span>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{c.body}</p>
                <div className="mt-3 grid sm:grid-cols-[160px_1fr] gap-2 items-start">
                  <Select value={c.status} onValueChange={(v) => update(c.id, { status: v })}>
                    <SelectTrigger data-testid={`status-${c.id}`} className="border-2 border-black bg-spotter-cream"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Textarea data-testid={`reply-${c.id}`} defaultValue={c.admin_reply || ""} placeholder="Write a reply to the member…"
                      className="border-2 border-black min-h-[44px]" id={`reply-input-${c.id}`} />
                    <Button data-testid={`save-reply-${c.id}`} onClick={() => update(c.id, { admin_reply: document.getElementById(`reply-input-${c.id}`).value })}
                      className="bg-spotter-maroon text-spotter-cream border-2 border-black">Reply</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>}
    </div>
  );
}
